import { FC, useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Container,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useCutsheetCapabilityManyQuery,
  useCutsheetCapabilityUpsertMutation,
} from 'lib/graphql';
import { useUserState } from 'providers/user-state-provider';
import { CutsheetSpeciesManagement } from './cutsheet-species-management';

const CUTSHEETS_BASE_PATH = '/processor/cutsheets-v2';

/**
 * Cut Sheet Builder — unified landing page.
 *
 * Loads the species management view directly, with a dropdown selector
 * in the tab bar when the vendor processes multiple species.
 * Auto-creates capability records for any enabled species that don't
 * have one yet.
 */
export const CutsheetCapabilitiesList: FC = () => {
  const { vendor } = useUserState();

  // Fetch existing capabilities for this vendor
  const {
    data: capabilityData,
    loading,
    refetch,
  } = useCutsheetCapabilityManyQuery({
    variables: {
      vendorId: vendor?._id,
      isActive: true,
    },
    skip: !vendor?._id,
  });

  const [createCapability] = useCutsheetCapabilityUpsertMutation();
  const [autoCreating, setAutoCreating] = useState(false);

  // Species the vendor has enabled in processor settings
  const enabledSpecies = useMemo(
    () =>
      vendor?.processorSettings?.animalSettings
        ?.filter((s) => s?.isEnabled)
        .map((s) => s!.species as string) ?? [],
    [vendor],
  );

  // Existing capabilities — sort so ones that have been edited
  // (updatedAt differs from createdAt) come first
  const capabilities = useMemo(() => {
    const caps = (capabilityData?.CutsheetCapabilityByVendorAndAnimal?.filter(
      Boolean,
    ) ?? []) as Array<{
      _id: string;
      name?: string | null;
      species?: string | null;
      createdAt?: string | null;
      updatedAt?: string | null;
    }>;

    // Sort oldest first — capabilities you've been working on longest
    // appear before newly auto-created empty ones
    return [...caps].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return aTime - bTime;
    });
  }, [capabilityData]);

  // Species that already have a capability record
  const coveredSpecies = useMemo(
    () => capabilities.map((c) => c.species).filter(Boolean) as string[],
    [capabilities],
  );

  // Auto-create capability records for enabled species that don't have one
  useEffect(() => {
    if (loading || autoCreating || !vendor?._id) return;

    const uncovered = enabledSpecies.filter((s) => !coveredSpecies.includes(s));
    if (uncovered.length === 0) return;

    const createMissing = async () => {
      setAutoCreating(true);
      for (const species of uncovered) {
        const name = species.charAt(0).toUpperCase() + species.slice(1);
        await createCapability({
          variables: {
            vendorId: vendor._id!,
            species,
            name,
            isActive: true,
          },
        });
      }
      await refetch();
      setAutoCreating(false);
    };
    createMissing();
  }, [
    loading,
    autoCreating,
    enabledSpecies,
    coveredSpecies,
    vendor,
    createCapability,
    refetch,
  ]);

  // URL-driven species selection.
  // Path: /processor/cutsheets-v2/:species → select the matching capability.
  // When no :species is in the URL yet, redirect (replace) to the first
  // available species so deep links are always scoped.
  const { species: speciesParam } = useParams<{ species?: string }>();
  const navigate = useNavigate();

  const selectedCapability = useMemo(() => {
    if (!capabilities.length) return null;
    if (speciesParam) {
      return (
        capabilities.find(
          (c) => c.species?.toLowerCase() === speciesParam.toLowerCase(),
        ) ?? null
      );
    }
    return null;
  }, [capabilities, speciesParam]);

  useEffect(() => {
    if (!capabilities.length) return;
    // Redirect to the first species if none in URL, or if the URL species
    // doesn't resolve to a known capability (e.g. stale/broken link).
    if (!speciesParam || !selectedCapability) {
      const firstSpecies = capabilities[0].species;
      if (firstSpecies) {
        navigate(`${CUTSHEETS_BASE_PATH}/${firstSpecies}`, { replace: true });
      }
    }
  }, [capabilities, speciesParam, selectedCapability, navigate]);

  const selectedCapabilityId = selectedCapability?._id ?? null;

  // Build the species dropdown for the tab bar (only when multiple species)
  const speciesSelector =
    capabilities.length > 1 ? (
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <Select
          value={selectedCapability?.species ?? ''}
          onChange={(e) => navigate(`${CUTSHEETS_BASE_PATH}/${e.target.value}`)}
          sx={{ textTransform: 'capitalize' }}
        >
          {capabilities.map((cap) => {
            const label = cap.species
              ? cap.species.charAt(0).toUpperCase() + cap.species.slice(1)
              : cap.name;
            return (
              <MenuItem
                key={cap._id}
                value={cap.species ?? ''}
                sx={{ textTransform: 'capitalize' }}
              >
                {label}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    ) : null;

  // Loading state
  if (loading || autoCreating) {
    return (
      <Container maxWidth="lg">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={300}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // No species configured
  if (capabilities.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
            bgcolor: 'grey.50',
            borderRadius: 2,
            mt: 4,
          }}
        >
          <Typography variant="h6" gutterBottom>
            No species configured
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Enable the species you process in your Processor Settings to get
            started.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {selectedCapabilityId && (
        <CutsheetSpeciesManagement
          capabilityIdOverride={selectedCapabilityId}
          speciesSelector={speciesSelector}
        />
      )}
    </Container>
  );
};
